<template>
  <div v-if="isLoggedIn">
    <a href="#main-content" class="k-navbar-skip-link" @click="skipToMainContent">Skip to main content</a>
    <nav class="k-navbar" role="navigation" aria-label="Primary">
    <div class="k-navbar-inner">
      <div class="k-navbar-header">
        <div class="k-navbar-brand">
          <router-link :to="homeRoute" class="k-navbar-brand-link" aria-label="Go to home page">
            <img :src="kateLogoImage" alt="" aria-hidden="true"/>
          </router-link>
          <sidebar-toggle class="k-navbar-sidebar-toggle"></sidebar-toggle>
          <environment-banner></environment-banner>
        </div>

        <button
          type="button"
          class="k-navbar-toggle"
          :class="{ 'is-active': !hideMobileNavbar }"
          :aria-expanded="(!hideMobileNavbar).toString()"
          :aria-label="hideMobileNavbar ? 'Open navigation menu' : 'Close navigation menu'"
          aria-controls="k-navbar-menu"
          @click="toggleMobileNavbar"
        >
          <i class="fas" :class="{ 'fa-bars': hideMobileNavbar, 'fa-xmark': !hideMobileNavbar }" aria-hidden="true"></i>
        </button>
      </div>

      <div id="k-navbar-menu" class="k-navbar-panels" :class="{ 'is-collapsed': hideMobileNavbar }"
        @keydown.esc="closeNavigationOverlays"
      >
        <section class="k-navbar-section k-navbar-section--navigation" aria-labelledby="k-navbar-navigation-title">
          <h2 id="k-navbar-navigation-title" class="k-navbar-section-title">Navigation</h2>
          <ul v-if="!store.isDashboard && programmeId" class="k-navbar-navigation" role="list">
            <li class="k-navbar-item" v-if="$featureFlags.isEnabled('kate-bot', 'navbar')">
              <router-link
                :to="{ name: 'chatbot_page', params: { programmeId: programmeId } }"
                class="k-navbar-link k-navbar-link--chat"
                :class="{ 'is-current': isPrimaryNav('chat') }"
                :aria-current="isPrimaryNav('chat') ? 'page' : undefined"
              >
                <img src="../../img/icons/kate-bot-chat-colours.svg" alt="" aria-hidden="true"/>
                <span class="k-navbar-label">Ask KATE</span>
              </router-link>
            </li>
            <li v-if="$featureFlags.isEnabled('learner-homepage')" class="k-navbar-item">
              <router-link :to="{ name: 'home', params: { programmeId: programmeId } }" class="k-navbar-link">
                <i class="far fa-house-blank" title="Home" aria-hidden="true"></i>
                <span class="k-navbar-label">Home</span>
              </router-link>
            </li>
            <li class="k-navbar-item">
              <router-link
                :to="{ name: 'programme_page', params: { programmeId: programmeId }}"
                class="k-navbar-link"
                :class="{ 'is-current': isPrimaryNav('curriculum') }"
                :aria-current="isPrimaryNav('curriculum') ? 'page' : undefined"
              >
                <i class="fas fa-book-open-cover" title="Curriculum" aria-hidden="true"></i>
                <span class="k-navbar-label">Curriculum</span>
              </router-link>
            </li>
            <otj-dropdown
              v-if="isApprenticeship"
              :programmeId="programmeId"
              :isOpen="showOtjDropdown"
              class="k-navbar-item k-navbar-item--dropdown"
              @open="showOtjDropdown = true"
              @close="showOtjDropdown = false"
            ></otj-dropdown>
          </ul>
        </section>

        <section class="k-navbar-section k-navbar-section--tools" aria-labelledby="k-navbar-tools-title">
          <h2 id="k-navbar-tools-title" class="k-navbar-section-title">Tools</h2>
          <ul class="k-navbar-tools" role="list">
            <k-theme-toggle title="Theme" class="k-navbar-item"></k-theme-toggle>
            <li v-if="!store.isDashboard && programmeId" class="k-navbar-item">
              <router-link :to="{ name: 'programme_schedule', params: { programmeId: programmeId }}" class="k-navbar-link btn-icon" title="Schedule">
                <i class="fas fa-calendar-alt" title="Schedule" aria-hidden="true"></i>
              </router-link>
            </li>
            <user-search-button v-if="isUserSearchAccessible && store.isDashboard" class="k-navbar-item"></user-search-button>
            <notification-dropdown
              :isOpen="showNotificationDropdown"
              :isDashboard="store.isDashboard"
              :isLoggedIn="isLoggedIn"
              class="k-navbar-item notification-dropdown"
              @open="openNotificationDropdown"
              @close="closeNotificationDropdown"
            ></notification-dropdown>
          </ul>
        </section>

        <section class="k-navbar-section k-navbar-section--account" aria-labelledby="k-navbar-account-title">
          <h2 id="k-navbar-account-title" class="k-navbar-section-title">Account</h2>
          <ul class="k-navbar-account" role="list" :hidden="!showUserDropdown">
            <user-dropdown
              :isOpen="showUserDropdown"
              class="k-navbar-item user-dropdown"
              @open="openUserDropdown"
              @close="closeUserDropdown"
            ></user-dropdown>
          </ul>
        </section>
      </div>
    </div>
    </nav>
  </div>
</template>

<style>
/* || Skip Link */
.k-navbar-skip-link {
  position: fixed;
  top: 8px;
  left: 12px;
  z-index: 10000;
  padding: 10px 14px;
  border-radius: 8px;
  background-color: var(--kate-button-outlined-hover);
  color: var(--kate-type-dark);
  text-decoration: none;
  transform: translateY(-150%);
  transition: transform 0.2s ease;
}

.k-navbar-skip-link:focus-visible {
  transform: translateY(0);
  outline: 2px solid var(--kate-button-primary);
  color: var(--kate-type-dark);
  outline-offset: 2px;
}

/* || Base Navbar Shell */
.k-navbar {
  position: fixed;
  top: 0;
  right: 0;
  left: 0;
  z-index: 9997;
  min-height: 70px;
  margin: 0;
  border: 0;
  background-color: var(--kate-background);
  color: var(--kate-type-primary);
}

/* || Main Layout Grid */
.k-navbar-inner {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: 12px;
  padding: 0 12px;
  min-height: 70px;
}

/* || Header + Brand */
.k-navbar-header {
  display: flex;
  align-items: center;
  min-width: 0;
}

.k-navbar-brand {
  display: flex;
  align-items: center;
  min-width: 0;
}

.k-navbar-brand-link {
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  text-decoration-color: transparent;
}

.k-navbar-brand-link:focus-visible,
.k-navbar-toggle:focus-visible {
  outline-offset: var(--kate-focus-ring-offset);
}

.k-navbar-brand-link img {
  height: 40px;
}

.k-navbar-sidebar-toggle {
  margin-left: 10px;
}

/* || Mobile Toggle */
.k-navbar-toggle {
  display: none;
  margin-left: 10px;
  padding: 0 8px;
  border: 0;
  background: transparent;
  color: var(--kate-primary);
  font-size: 1.75rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.k-navbar-toggle:hover i {
  color: var(--kate-neon-primary-text);
  text-shadow: var(--kate-neon-primary-shadow);
  transition: all 0.2s ease;
}

.k-navbar-toggle.is-active i {
  color: var(--kate-neon-primary-text);
}

/* || Navigation + Tools Columns */
.k-navbar-panels {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

/* || Panel Sections */
.k-navbar-section {
  min-width: 0;
}

.k-navbar-section-title {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
  font-size: 0.8rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--kate-type-secondary);
}

.k-navbar-section--tools,
.k-navbar-section--account {
  justify-self: end;
}

.k-navbar-navigation,
.k-navbar-tools,
.k-navbar-account {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  align-items: center;
  gap: 2px;
}

.k-navbar-navigation {
  justify-content: center;
  min-width: 0;
}

.k-navbar-tools {
  justify-content: flex-end;
}

.k-navbar-account {
  justify-content: flex-end;
}

.k-navbar-item {
  position: relative;
}

.k-navbar-item > a > i,
.k-navbar-item > .btn-icon > i:first-child {
  font-size: 1.2em;
  bottom: 0;
}

.k-navbar-item > a > img {
  font-size: 1.2em;
  width: 1.2em;
  height: 1.2em;
}

/* || Interactive Controls */
.k-navbar-link,
.k-navbar .btn-icon,
.k-navbar .nav-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 48px;
  padding: 0 14px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--kate-type-primary);
  text-decoration: none;
  text-decoration-color: transparent;
  font-family: hk-grotesk-light, sans-serif;
  cursor: pointer;
}

/* || Interactive States */
.k-navbar-link:hover,
.k-navbar .btn-icon:hover,
.k-navbar .nav-link:hover {
  background-color: var(--kate-panel-alt);
  color: var(--kate-neon-primary-text);
}

.k-navbar-link:focus-visible,
.k-navbar .btn-icon:focus-visible,
.k-navbar .nav-link:focus-visible {
  outline: var(--kate-focus-ring-width) solid var(--kate-focus-ring-color);
  outline-offset: var(--kate-focus-ring-offset);
}

.k-navbar li.is-open > button,
.k-navbar .router-link-active,
.k-navbar .is-current {
  color: var(--kate-neon-primary-text);
  text-shadow: var(--kate-neon-primary-shadow);
}

/* || Open Dropdown Trigger State */
.k-navbar .is-open > .btn-icon,
.k-navbar .is-open > .k-navbar-link {
  background-color: var(--kate-panel-alt);
}

.k-navbar-link--chat img {
  width: 20px;
  height: 20px;
}

/* || Dropdown Panel */
.k-navbar .k-menu-panel {
  position: absolute;
  top: 100%;
  left: 50%;
  min-width: 200px;
  margin: 12px 0 0;
  padding: 0;
  list-style: none;
  border-radius: 8px;
  border: 1px solid var(--kate-panel-alt);
  background-color: var(--kate-background-alt);
  box-shadow: var(--box-shadow);
  transform: translate(-50%, -8px);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

/* || Dropdown Open State */
.k-navbar .k-menu-panel.is-open {
  opacity: 1;
  transform: translate(-50%, 0);
  pointer-events: auto;
}

/* || Dropdown Arrow */
.k-navbar .k-menu-panel::before {
  position: absolute;
  top: -8px;
  left: 50%;
  content: "";
  border-width: 6px;
  margin-top: -5px;
  border-style: solid;
  border-color: transparent transparent var(--kate-background-alt) transparent;
}

/* || Dropdown Items */
.k-navbar .k-menu-panel > .k-menu-item {
  border-bottom: 1px solid var(--kate-panel-alt);
}

.k-navbar .k-menu-panel > .k-menu-item:last-child {
  border-bottom: 0;
}

/* || Dropdown Menu Item Layout (uniform a + button) */
.k-navbar .k-menu-item > a,
.k-navbar .k-menu-item > button {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 14px;
  width: 100%;
  padding: 15px;
  color: var(--kate-type-primary);
  background: transparent;
  border: 0;
  border-radius: 0;
  text-align: left;
  text-decoration: none;
  font-family: hk-grotesk-light, sans-serif;
  line-height: 1.2;
}

.k-navbar .k-menu-item > a.router-link-active {
  color: var(--kate-neon-primary-text);
}

.k-navbar .k-menu-item > a .fa-caret-down,
.k-navbar .k-menu-item > button .fa-caret-down {
  margin-left: auto;
  width: auto;
}

.k-navbar .k-menu-item > a:hover,
.k-navbar .k-menu-item > button:hover {
  background-color: var(--kate-panel-alt);
  color: var(--kate-neon-primary-text);
}

.k-navbar .k-menu-item > a:focus-visible,
.k-navbar .k-menu-item > button:focus-visible {
  outline: var(--kate-focus-ring-width) solid var(--kate-focus-ring-color);
  outline-offset: -2px;
}

/* || Profile Dropdown Alignment */
.k-navbar .profile-page .user-avatar + .k-menu-panel {
  left: auto;
  right: 0;
  transform: translate(0, -8px);
}

.k-navbar .profile-page .user-avatar + .k-menu-panel.is-open {
  transform: translate(0, 0);
}

/* || Notification Dropdown Alignment */
.k-navbar .notification-item > a {
  display: block;
  text-decoration: none;
  padding: 15px;
  color: var(--kate-type-primary);
}

.k-navbar .notification-item > a:hover {
  background-color: var(--kate-panel-alt);
  text-decoration: underline;
}

.k-navbar .notification > .k-menu-panel {
  left: auto;
  right: 0;
  min-width: 300px;
  max-height: 30vh;
  transform: translate(0, -8px);
  overflow: auto;
}

.k-navbar .notification > .k-menu-panel.is-open {
  transform: translate(0, 0);
}

/* || Right-Aligned Dropdown Arrow */
.k-navbar .notification > .k-menu-panel::before {
  left: auto;
  right: 14px;
}

.k-navbar .notification .dropdown-container-arrow {
  display: none;
}

/* || Avatar + User Meta */
.k-navbar #navbar-avatar {
  width: 2.28em;
  height: 2.28em;
  margin-left: 8px;
  margin-right: 2px;
}

.k-navbar #logout-button {
  cursor: pointer;
}

/* || Dropdown Static Meta Rows */
.k-navbar .k-menu-meta {
  padding: 15px;
  color: var(--kate-type-primary);
  pointer-events: none;
}

.k-navbar .k-menu-meta b {
  font-family: hk-grotesk-light, sans-serif;
}

/* || Breakpoint: Large Desktop */
@media (min-width: 1286px) {
  .k-navbar-navigation .k-navbar-link i:first-child,
  .k-navbar-navigation .btn-icon i:first-child {
    display: none;
  }
}

/* || Breakpoint: Tablet */
@media (min-width: 768px) and (max-width: 1285px) {
  .k-navbar-navigation .k-navbar-link span,
  .k-navbar-navigation .btn-icon span,
  .k-navbar-tools .btn-icon span,
  .k-navbar .fa-caret-down {
    display: none;
  }

  .k-navbar-tools .btn-icon span.count-bubble {
    display: block;
  }

  .k-navbar-navigation .k-navbar-link,
  .k-navbar-tools .k-navbar-link,
  .k-navbar .btn-icon {
    min-width: 46px;
    padding: 0 10px;
  }
}

/* || Breakpoint: Mobile */
@media (max-width: 767px) {
  /* || Mobile Layout */
  .k-navbar-inner {
    grid-template-columns: 1fr;
    gap: 0;
    padding: 0 8px;
    box-shadow: var(--box-shadow);
  }

  .k-navbar-header {
    justify-content: space-between;
    min-height: 64px;
  }

  .k-navbar-brand-link img {
    height: 30px;
  }

  .k-navbar-toggle {
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
  }

  /* || Mobile Panels */
  .k-navbar-panels {
    display: block;
    border: 1px solid var(--kate-panel-alt);
    border-radius: 18px;
    background-color: var(--kate-background-alt-alpha);
    padding: 12px;
    margin-top: 2px;
    overflow: auto;
    max-height: 80vh;
  }

  .k-navbar-section {
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px solid var(--kate-panel-alt);
    width: 100%;
  }

  .k-navbar-panels.is-collapsed,
  .k-navbar-section-title:only-child {
    display: none;
  }

  .k-navbar-section:first-child {
    margin-top: 0;
    padding-top: 0;
    border-top: 0;
  }

  .k-navbar-section-title {
    position: static;
    width: auto;
    height: auto;
    margin: 0 0 10px;
    overflow: visible;
    clip: auto;
    clip-path: none;
    white-space: normal;
    border: 0;
    display: block;
    letter-spacing: 0.05em;
    font-family: hk-grotesk-light, sans-serif;
    color: var(--kate-type-alt-dark);
  }

  /* || Mobile Stacked Menus */
  .k-navbar-link,
  .k-navbar .btn-icon,
  .k-navbar .nav-link,
  .k-navbar .k-menu-item > a,
  .k-navbar .k-menu-item > button {
    justify-content: flex-start;
  }

  .k-navbar-navigation,
  .k-navbar-account {
    flex-direction: column;
    align-items: stretch;
    gap: 4px;
  }

  .k-navbar-tools {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
    align-items: stretch;
  }

  .k-navbar-item,
  .k-navbar-link,
  .k-navbar .btn-icon,
  .k-navbar .nav-link {
    width: 100%;
  }

  .k-navbar-section--tools .k-navbar-item {
    min-width: 0;
  }

  .k-navbar-section--account .user-avatar,
  .k-navbar-section--tools .k-navbar-link,
  .k-navbar-section--tools .btn-icon {
    min-height: 88px;
    border-radius: 14px;
    padding: 12px;
    gap: 8px;
    text-align: center;
    justify-content: center;
    flex-wrap: wrap;
    background-color: var(--kate-background-alpha);
  }

  .k-navbar-section--tools .btn-icon > i:first-child,
  .k-navbar-section--tools .k-navbar-link > i:first-child {
    flex: 1 1 100%;
  }

  .k-navbar-section--tools .btn-icon > i:not(:only-child):last-child {
    display: none;
  }

  /* || Mobile Tools: full-width notification panel */
  .k-navbar-section--tools .notification.is-open {
    display: contents;
  }

  .k-navbar-section--tools .notification.is-open > .btn-icon {
    grid-column: auto;
    width: 100%;
  }

  .k-navbar-section--tools .notification.is-open > .k-menu-panel {
    grid-column: 1 / -1;
    width: 100%;
    max-width: 100%;
    margin-top: 8px;
  }

  .k-navbar-section--account .user-avatar {
    justify-content: flex-start;
  }

  /* || Mobile Dropdown Behavior */
  .k-navbar .k-menu-panel {
    position: static;
    margin-top: 6px;
    border-radius: 6px;
    transform: none;
    opacity: 1;
    pointer-events: auto;
    display: none;
    box-shadow: none;
    min-width: 100%;
    background-color: var(--kate-background-alt-alpha);
  }

  .k-navbar .k-menu-panel.is-open {
    display: block;
    transform: none;
  }

  .k-navbar .k-menu-panel::before {
    display: none;
  }

  .k-navbar .k-menu-item > a,
  .k-navbar .k-menu-item > button {
    min-height: 64px;
    padding: 16px 18px;
  }

  .k-navbar .k-menu-panel .support-dropdown-btn,
  .k-navbar .k-menu-panel .otj-dropdown-btn {
    min-height: 64px;
    padding: 16px 18px;
  }

  .k-menu-item:focus-visible {
    outline: var(--kate-focus-ring-width) solid var(--kate-focus-ring-color);
    outline-offset: -2px;
  }
}
</style>

<script>
import useGlobalStore from '@stores/global';
import useThemeStore from '@stores/theme';
import useAppProgrammeStore from '@stores/app-programme-store';
import SidebarMixin from '@mixins/sidebar-mixin';
import { importImage } from '@utils/dynamic-image-importer';
import UserDropdown from './navbar-dropdowns/user-dropdown.vue';
import NotificationDropdown from './navbar-dropdowns/notification-dropdown.vue';
import OtjDropdown from './navbar-dropdowns/otj-dropdown.vue';
import SidebarToggle from './sidebar-toggle.vue';
import KThemeToggle from './k-theme-toggle.vue';
import EnvironmentBanner from './environment-banner.vue';
import UserSearchButton from './user-search-button.vue';

export default {
  components: {
    UserSearchButton,
    UserDropdown,
    NotificationDropdown,
    SidebarToggle,
    KThemeToggle,
    EnvironmentBanner,
    OtjDropdown,
  },

  mixins: [SidebarMixin],

  data() {
    return {
      store: useGlobalStore(),
      themeStore: useThemeStore(),
      appProgrammeStore: useAppProgrammeStore(),
      hideMobileNavbar: true,
      showUserDropdown: false,
      showNotificationDropdown: false,
      showOtjDropdown: false,
    };
  },

  mounted() {

  },

  beforeUnmount() {

  },

  computed: {
    isUserSearchAccessible() {
      return (this.store.userPermissions.includes('view_all_learners') || this.store.userPermissions.includes('view_restricted_learners'));
    },
    isLoggedIn() {
      return this.store.isLoggedIn;
    },
    homeRoute() {
      if (this.store.isDashboard) {
        return { name: 'dashboard_home' };
      }
      return { name: 'programme_page', params: { programmeId: this.programmeId } };
    },
    kateLogoImage() {
      if (this.themeStore.theme !== 'light') {
        return importImage('kate_full_logo.svg');
      }
      return importImage('kate_full_logo_dark.svg');
    },
    programmeId() {
      if (this.store.isDashboard) {
        // Don't persist programmeId on dashboard
        return null;
      }
      return this.appProgrammeStore.currentProgrammeId;
    },

    isApprenticeship() {
      return this.appProgrammeStore.currentProgramme?.is_apprenticeship;
    },
  },

  methods: {
    isPrimaryNav(section) {
      return this.$route.matched.some(r => r.meta.primaryNav === section);
    },

    toggleMobileNavbar() {
      this.hideMobileNavbar = !this.hideMobileNavbar;
    },

    closeNavigationOverlays() {
      this.hideMobileNavbar = true;
      this.showUserDropdown = false;
      this.showNotificationDropdown = false;
      this.showOtjDropdown = false;
    },

    findMainContentTarget() {
      return document.querySelector('main, [role="main"], #main-content');
    },

    skipToMainContent(event) {
      const target = this.findMainContentTarget();

      if (!target) {
        return;
      }

      event.preventDefault();
      this.closeNavigationOverlays();

      if (!target.hasAttribute('tabindex')) {
        target.setAttribute('tabindex', '-1');
      }

      target.focus({ preventScroll: true });
      target.scrollIntoView({ block: 'start' });
    },

    openUserDropdown() {
      this.showUserDropdown = true;
      this.showNotificationDropdown = false;
    },

    openNotificationDropdown() {
      this.showNotificationDropdown = true;
      this.showUserDropdown = false;
    },
    closeNotificationDropdown() {
      this.showNotificationDropdown = false;
    },

    closeUserDropdown() {
      this.showUserDropdown = false;
    },
  },

  watch: {
    $route() {
      this.closeNavigationOverlays();
    },
  },
};
</script>
